// Неразрывные пробелы как в DOCX: сборщик (writer/builder.py typo() -> lib/sto_lib.typography, TYPO_RULES) при
// экспорте ставит неразрывный пробел после предлогов и союзов «и», «а», между «рисунок/таблица/листинг/приложение»
// и номером, «№» и числом, числом и единицей, перед тире… В тексте редактора этих пробелов может не быть (импорт,
// вставка), и тогда строки переносятся не так, как в Word. Здесь - только отображение: такие пары слов
// обёрнуты декорацией .pg-nb (white-space: nowrap), модель документа не меняется.
// Всё это - только при USE_NBSP (core/schema/constants, как lib/sto_lib.USE_NBSP). Пока он выключен, writer ставит
// обычные пробелы (и неразрывные из текста выгружает обычными) - декораций нет, строки переносятся по обычным
// пробелам, как в Word.
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { USE_NBSP, plainSpaces } from '../../core/schema/constants';

const W = '[\\p{L}\\p{N}_]';
const PREPS = 'из-за|из-под|в|во|к|ко|с|со|у|о|об|обо|на|по|за|из|от|до|для|при|без|под|над|про|через|перед|передо|после|'
  + 'между|около|среди|вдоль|кроме|возле|вокруг|против|сквозь|ради|и|а';
const FIG = 'рисун(?:ок|ка|ку|ком|ке|ки|ков|кам|ками|ках)';
const TBL = 'таблиц(?:а|ы|е|у|ей|ею|ам|ами|ах)?';
const UNITS = '(?:байт(?:а|ов|ы)?|бит(?:а|ов|ы)?|[КМГТ]байт(?:а|ов)?|[КкМГТ]Б|[КкМГ]бит/с|бит/с|[КМГТ]иБ|'
  + 'мс|мкс|нс|сек|мин|[кМГ]?Гц|мВ|кВ|мА|к?Вт|[кМ]?Ом|мм|см|км|кг|мг|°[CС]|пт|dpi|px|пикс(?:\\.|елей|еля)?|'
  + 'ms|[KMGT]B|[KMG]bps|[KMG]b/s|[KMG]?Hz|ГБ|МБ|КБ|%)';

/** Правила: регулярное выражение, группа «пробела» (индекс совпадения пробела внутри match) - первый пробел в совпадении. */
const RULES: RegExp[] = [
  new RegExp(`(?<![\\p{L}\\p{N}_\\-.])(?:${PREPS}) (?=[\\p{L}\\p{N}_«(\\d\\ue000\\ue001−|])`, 'giu'),
  new RegExp(`(?<![А-Яа-яЁё])(?:${FIG}|${TBL}|листинг${W}*|приложени${W}*) +(?=\\d|[А-Я](?!${W})|[А-Я]\\.\\d)`, 'giu'),
  /№ (?=\d)/gu,
  new RegExp(`(?<![\\p{L}\\p{N}_.,])\\d+(?:[.,]\\d+)? ${UNITS}(?![\\p{L}\\p{N}_/])`, 'gu'),
  /(?<=\S) (?=–[  ])/gu,
  new RegExp(`(?<=\\d) (?=(?:с|p|pp|стр|г|гг|тыс|млн|млрд|руб|коп)\\.?(?!${W}))`, 'gu'),
  new RegExp(`(?<!${W})(?:с|С|p|P|п|пп|гл|ч|ст|рис|табл|т|вып|разд)\\. (?=\\d)`, 'gu'),
  new RegExp(`(?<!${W})(?:[Фф]ормул|[Лл]истинг|[Пп]риложени)${W}* (?=\\((?:[А-ЯЁA-Z]\\.)?\\d|(?:[А-ЯЁA-Z]\\.)?\\d|[А-ЯЁA-Z](?!${W}))`, 'gu'),
  // инициалы и фамилия: «А. Д. Шилов», «Шилов А. Д.»
  /(?<![\p{L}\p{N}_.])[A-ZА-ЯЁ]\. (?=[A-ZА-ЯЁ])/gu,
  /(?<![\p{L}\p{N}_])[А-ЯЁA-Z][а-яёa-z]+ (?=[A-ZА-ЯЁ]\.[  ]?[A-ZА-ЯЁ]\.(?![\p{L}\p{N}_]))/gu,
  /(?<=\S) (?=\/{1,2}[  ])/gu,
];

const SKIP = new Set(['listing', 'codeBlock']);

/** Текст узла-абзаца с позициями: атомы - заменители (ссылка на объект - «0», формула - U+E000). */
function textOf(block: PMNode, start: number): { text: string; pos: number[] } {
  let text = '';
  const pos: number[] = [];
  block.forEach((ch, off) => {
    const p = start + 1 + off;
    if (ch.isText) {
      const t = ch.text || '';
      for (let i = 0; i < t.length; i++) { text += t[i]; pos.push(p + i); }
    } else {
      const n = ch.type.name;
      const c = n === 'xref' ? '0' : n === 'mathInline' ? '' : n === 'hardBreak' ? '\n' : n === 'cite' ? '[' : '';
      text += c;
      pos.push(p);
    }
  });
  return { text, pos };
}

function blockDecos(block: PMNode, start: number, out: Decoration[]) {
  const { text, pos } = textOf(block, start);
  if (text.indexOf(' ') < 0 && text.indexOf(' ') < 0) return;
  const spaces: number[] = [];
  // уже неразрывные пробелы: в Word они не сжимаются при выравнивании по ширине - тоже в .pg-nb
  for (let i = text.indexOf(' '); i >= 0; i = text.indexOf(' ', i + 1)) spaces.push(i);
  for (const re of RULES) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      const sp = text.indexOf(' ', m.index);
      if (sp >= 0 && sp <= m.index + m[0].length) spaces.push(sp);
      if (m[0].length === 0) re.lastIndex++;
    }
  }
  if (!spaces.length) return;
  spaces.sort((a, b) => a - b);
  // диапазон: от начала слова перед пробелом до конца слова после; пересекающиеся - вместе
  const ranges: [number, number][] = [];
  for (const s of spaces) {
    let a = s;
    while (a > 0 && !/[ \n]/.test(text[a - 1])) a--;
    let b = s + 1;
    while (b < text.length && !/[ \n]/.test(text[b])) b++;
    const last = ranges[ranges.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else ranges.push([a, b]);
  }
  for (const [a, b] of ranges) {
    if (b <= a) continue;
    const from = pos[a];
    const to = b < pos.length ? pos[b - 1] + 1 : pos[pos.length - 1] + 1;
    if (to > from) out.push(Decoration.inline(from, to, { class: 'pg-nb' }));
  }
}

function build(doc: PMNode, from = 0, to = doc.content.size, out: Decoration[] = []): Decoration[] {
  doc.nodesBetween(from, to, (n, p) => {
    if (SKIP.has(n.type.name)) return false;
    if (n.isTextblock) { blockDecos(n, p, out); return false; }
    return true;
  });
  return out;
}

const key = new PluginKey<DecorationSet>('stoPagesNbsp');

export const NbspExtension = Extension.create({
  name: 'stoPagesNbsp',
  addProseMirrorPlugins() {
    if (!USE_NBSP) return [];              // неразрывных пробелов в DOCX нет - и связок .pg-nb нет
    return [new Plugin<DecorationSet>({
      key,
      state: {
        init: (_, state) => DecorationSet.create(state.doc, build(state.doc)),
        apply(tr, set) {
          if (!tr.docChanged) return set;
          let next = set.map(tr.mapping, tr.doc);
          // изменённые абзацы - заново
          const ranges: [number, number][] = [];
          tr.mapping.maps.forEach((m, i) => {
            m.forEach((_a, _b, s, e) => {
              let a = s; let b = e;
              for (let j = i + 1; j < tr.mapping.maps.length; j++) { a = tr.mapping.maps[j].map(a, -1); b = tr.mapping.maps[j].map(b, 1); }
              ranges.push([a, b]);
            });
          });
          for (const [a0, b0] of ranges) {
            const a = Math.max(0, Math.min(a0, tr.doc.content.size));
            const b = Math.max(a, Math.min(b0, tr.doc.content.size));
            const $a = tr.doc.resolve(a);
            const $b = tr.doc.resolve(b);
            const from = $a.depth > 0 ? $a.start(Math.min($a.depth, 1)) - 1 : a;
            const to = $b.depth > 0 ? $b.end(Math.min($b.depth, 1)) + 1 : b;
            const f = Math.max(0, from);
            const t = Math.min(tr.doc.content.size, to);
            next = next.remove(next.find(f, t));
            next = next.add(tr.doc, build(tr.doc, f, t));
          }
          return next;
        },
      },
      props: { decorations: (s) => key.getState(s) },
    })];
  },
});

/** Строка так, как её выведет writer (typo(): lib/sto_lib.TYPO_RULES) - для текста вне ProseMirror (список источников):
 *  без пробела перед знаками препинания; при USE_NBSP - неразрывные пробелы после инициалов, перед тире, в «633 с.»,
 *  «// Журнал»… (без него - обычные пробелы, неразрывные из текста - тоже обычными). */
export function typoText(s: string): string {
  // без USE_NBSP writer не ставит неразрывных пробелов, а пришедшие в тексте выгружает обычными
  if (!USE_NBSP) s = plainSpaces(s);
  let t = s.replace(/(?<=[^\s])[  ]+(?=[,;:!?)»]|\.(?![\p{L}\p{N}_.]))/gu, '').replace(/(?<=[(«])[  ]+/gu, '');
  if (!USE_NBSP) return t;
  const idx = new Set<number>();
  const extra = [/(?<=[а-яёa-z]), (?=[A-ZА-ЯЁ]\.(?:[  ]?[A-ZА-ЯЁ]\.)?(?![\p{L}\p{N}_]))/gu];
  for (const re of [...RULES, ...extra]) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(t))) {
      const sp = t.indexOf(' ', m.index);
      if (sp >= 0 && sp <= m.index + m[0].length) idx.add(sp);
      if (m[0].length === 0) re.lastIndex++;
    }
  }
  if (!idx.size) return t;
  const a = [...t];
  // индексы - в UTF-16; кириллица и латиница - по одной единице, суррогатных пар в описаниях источников нет
  for (const i of idx) if (t[i] === ' ') a[i] = ' ';
  return a.join('');
}

/** Части строки для показа: слова, связанные неразрывными пробелами, - отдельными кусками (их оборачивают в .pg-nb:
 *  неразрывный пробел в Word не сжимается и не растягивается при выравнивании по ширине, в браузере word-spacing
 *  сжимал бы и его). */
export function nbspParts(t: string): { text: string; nb: boolean }[] {
  const out: { text: string; nb: boolean }[] = [];
  const re = /[^  ]*(?: [^  ]*)+/gu;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(t))) {
    if (!m[0]) { re.lastIndex++; continue; }
    if (m.index > last) out.push({ text: t.slice(last, m.index), nb: false });
    out.push({ text: m[0], nb: true });
    last = m.index + m[0].length;
  }
  if (last < t.length) out.push({ text: t.slice(last), nb: false });
  return out;
}
